import { useState, useRef, useEffect, Fragment } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Button, InputBase, Typography } from '@material-ui/core';
import ChevronIcon from 'components/common/ChevronFaIcon';
import Chips from 'components/common/Chips';
import styles from './styles.scss';
import { isFunction } from 'lodash';

export default function CustomizedMenus({
    label,
    placeholder,
    inputPlaceHolder,
    options,
    isRequired,
    handleChange,
    buttonLabel,
    onButtonClicked,
    onSelectedOption,
    onClose,
    customFilter,
    renderChild,
    isSearchable = true,
    listPosition,
    listWidth,
    Tooltip,
    onListOpened,
    chipData,
    subLabel,
    onChipLabelRemoved,
    isReadOnly,
    showInput = true,
    ...props }) {
    const [newDataSource, setNewDataSource] = useState('');
    const [filterInput, setFilterInput] = useState('');
    const [value, setValue] = useState(false);
    const [filteredOptions, setFilteredOptions] = useState(null);
    const [showList, setShowList] = useState(false);

    const useStyles = makeStyles((theme) => ({
        Button: {
            width: '100%',
            height: '40px',
            display: 'flex',
            justifyContent: 'space-between',
            border: '1px solid #B1B1B1',
            padding: '0 10px',
            fontFamily: 'Hind',
            color: '#28282E',
            borderRadius: '4px',
            '&:active, &:focus, &:hover': {
                color: '#28282E',
                boxShadow: 'unset',
                borderColor: '#2057AD'
            },
            '&>input': !value && {
                '&::placeholder': {
                    fontFamily: 'Hind !important',
                    fontSize: '16px !important',
                    fontWeight: '400 !important',
                    lineHeight: '23.59px !important',
                    fontStyle: 'italic !important',
                    color: '#B1B1B1 !important'
                }
            }
        },
        header: {
            color: '#28282E',
            fontFamily: 'Hind',
            fontSize: '1rem',
            fontStyle: 'normal',
            fontWeight: 500,
            lineHeight: '20px',
            paddingBottom: '5px',
        },
        subLabel: {
            color: 'var(--grey-600, #B1B1B1)',
            fontFamily: 'Hind',
            fontSize: '1rem',
            fontStyle: 'normal',
            fontWeight: 500,
            lineHeight: '20px',
            paddingBottom: '5px',
        },
        Required: {
            color: '#F1913B',
        },
        ListRoot: {
            border: '1px solid #d3d4d5',
            width: listWidth ? listWidth : '600px',
            maxHeight: '215px',
            overflowX: 'hidden',
            fontSize: '16px',
            fontFamily: 'Hind',
            fontWeight: '400',
            fontStyle: 'normal',
            lineHeight: '20px',
            color: '#28282E',
            position: listPosition ? listPosition : 'absolute',
            zIndex: 9,
            backgroundColor: '#fff',
            '& ul li:hover': {
                backgroundColor: '#DEEBFF',
            },
            '& ul li:active': {
                backgroundColor: '#B2D4FF'
            }
        },
        InnerInput: {
            fontSize: '16px',
            fontFamily: 'Hind',
            fontWeight: '400',
            fontStyle: 'normal',
            lineHeight: '20px',
            padding: 0,
            paddingTop: '3px',
            height: 'auto',
        },
        buttonLabel: {
            fontStyle: 'normal',
            fontWeight: 400,
            lineHeight: '20px',
            color: '#28282E',
            fontSize: '16px',
            textTransform: 'none',
        },
        ListItems: {
            display: 'flex',
            alignItems: 'center',
            height: '40px',
            padding: '0px 10px 0px 10px',
            gap: '10px',
            cursor: 'pointer',
            fontFamily: 'Hind',
            fontWeight: '400',
            fontSize: '16px',
            lineHeight: '20px'
        }
    }));

    const classes = useStyles();

    useEffect(() => {
        function handleClickOutside(event) {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setShowList(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [wrapperRef]);

    const wrapperRef = useRef(null);

    useEffect(() => {
        setValue(props.value);
    }, [props.value]);

    useEffect(() => {
        if (!showList) {
            setFilterInput('');
            if (isFunction(onClose)) {
                onClose();
            }
        }

        if (showList && isFunction(onListOpened)) {
            onListOpened();
        }

    }, [showList]);

    useEffect(() => {
        let filteredValues = [...options];
        if (customFilter) {
            filteredValues = customFilter(filterInput);
        } else if (!customFilter && filterInput) {
            filteredValues = options.filter(option => option.value.toLowerCase().includes(filterInput.toLowerCase()));
        }

        setFilteredOptions(filteredValues);
    }, [filterInput, options]);

    function handleNewDataSource(event) {
        const { value } = event.target;
        setNewDataSource(value);
    }

    function handleOnClick() {
        onButtonClicked(newDataSource, () => onSuccess());
        setNewDataSource('');
    }

    function handleOnClose() {
        if (showList) {
            setShowList(false);
        }
        setFilterInput('');
    }

    function onSuccess() {
        setShowList(false);
        setValue(newDataSource);
    }
    function handleOptionSelected(value) {
        setFilterInput('');
        setShowList(false);
        const getDisplay = value.label || value.text || value.value || value.name || value;
        setValue(getDisplay);
        onSelectedOption(value);
    }

    function isOptionSelected(option) {
        if (!value) return;
        return (
            (option.text && option.text === value) ||
            (option.label && option.label === value) ||
            (option.value && option.value === value) ||
            (option.name && option.name === value)
        );
    }

    function hanleShowList() {
        setShowList(!showList);
    }

    return (
        <div className={styles.container} style={{ width: '100%', paddingTop: '10px', position: 'relative' }}>
            {
                !!label && (
                    <Typography className={classes.header}>
                        {label} {' '}
                        {isRequired && (<span className={classes.Required}>*Required{' '}</span>)}
                        {Tooltip && (<Tooltip />)}
                    </Typography>
                )
            }
            {
                subLabel && (
                    <Typography className={classes.subLabel}>{subLabel}</Typography>
                )
            }
            {
                chipData && (
                    <Chips data={chipData} isDeletable={!isReadOnly} handleDelete={onChipLabelRemoved} />
                )
            }
            {
                isReadOnly && (
                    <p>{value ? value : '-'}</p>
                )
            }
            {
                !isReadOnly && showInput && (
                    <Fragment>
                        {
                            isSearchable ? (
                                <InputBase
                                    id='baseSearchBox'
                                    autoComplete='off'
                                    placeholder={value ? value : placeholder}
                                    onClick={hanleShowList}
                                    onChange={(e) => setFilterInput(e.target.value)}
                                    classes={{
                                        root: classes.Button
                                    }}
                                    style={{ borderColor: `${props.notValid ? '#CC3333' : ''}` }}
                                    inputProps={{
                                        className: classes.InnerInput,
                                        value: filterInput
                                    }}
                                    endAdornment={<ChevronIcon isOpen={Boolean(false)} />}
                                />
                            ) : (
                                <Button
                                    variant='outlined'
                                    disableRipple={true}
                                    aria-controls='customized-menu'
                                    aria-haspopup='true'
                                    onClick={hanleShowList}
                                    classes={{
                                        label: classes.buttonLabel,
                                        root: classes.Button
                                    }}
                                    style={{ borderColor: `${props.notValid ? '#CC3333' : ''}` }}
                                    endIcon={<ChevronIcon customStyles={{ padding: '4px', paddingRight: '4px' }} isOpen={false} />}>
                                    {value ? value : placeholder}
                                </Button>
                            )
                        }
                    </Fragment>
                )
            }


            <Fragment>
                {
                    showList && (
                        <div
                            ref={wrapperRef}
                            className={classes.ListRoot}
                        >
                            {
                                !renderChild ? (
                                    <ul>
                                        {
                                            filteredOptions.map((el, index) => (
                                                <li className={classes.ListItems} style={{ backgroundColor: `${isOptionSelected(el) && '#2684FF'}`, color: `${isOptionSelected(el) && '#FFF'}` }} onClick={() => handleOptionSelected(el)} key={`${index * Math.random()}_${el.label}}`}>{el.text || el.label || el.name}</li>
                                            ))
                                        }
                                    </ul>
                                ) : (
                                    renderChild({ handleOptionSelected, classes, filteredOptions, handleOnClick, handleNewDataSource, newDataSource, onSelectedOption, handleOnClose, isOptionSelected })
                                )
                            }
                        </div>
                    )
                }
            </Fragment>
        </div>
    );
}
